import posthog from "posthog-js";
import type { ProductEvent } from "@/lib/analytics/events";
import type { AnalyticsEnvironment } from "@/lib/analytics/env";

let captureEnabled = false;
let captureEnvironment: AnalyticsEnvironment = "development";

/**
 * Internal — set by <PostHogProvider />. Do not call from app code.
 */
export function _setCaptureEnabled(value: boolean): void {
  captureEnabled = value;
}

/**
 * Internal — set by <PostHogProvider />. Do not call from app code.
 */
export function _setCaptureEnvironment(value: AnalyticsEnvironment): void {
  captureEnvironment = value;
}

/**
 * Capture a typed product event from a Client Component.
 * No-op when the posthog-js singleton has not been initialized
 * (e.g. POSTHOG_PROJECT_TOKEN missing in dev/test, or before
 * <PostHogProvider /> mounts).
 */
export function captureClient(event: ProductEvent): void {
  if (!captureEnabled) return;
  if (!posthog.__loaded) return;
  posthog.capture(event.name, {
    ...event.properties,
    environment: captureEnvironment,
  });
}

export interface IdentifyTraits {
  email: string;
  organizationId: string;
  organizationSlug: string;
}

/**
 * Set the PostHog distinctId to the Skrift `User.id` and attach user traits.
 * Idempotent — `<PostHogIdentify />` calls this once per session change.
 * No-op until `<PostHogProvider />` has called `posthog.init()`.
 */
export function identifyClient(
  skriftUserId: string,
  traits: IdentifyTraits
): void {
  if (!captureEnabled) return;
  if (!posthog.__loaded) return;
  posthog.identify(skriftUserId, traits);
}

/**
 * Associate subsequent client-side events with an organization group so
 * org-level retention and funnels work in PostHog. No-op until init.
 */
export function groupClient(
  organizationId: string,
  props: { name: string; slug: string }
): void {
  if (!captureEnabled) return;
  if (!posthog.__loaded) return;
  posthog.group("organization", organizationId, props);
}

/**
 * Capture an unhandled client-side exception. Routes to PostHog's
 * `$exception` event — the same channel `captureServerException` uses on
 * the server, so client and server errors share dashboards. No-op until init.
 */
export function captureClientException(error: unknown): void {
  if (!captureEnabled) return;
  if (!posthog.__loaded) return;
  posthog.captureException(error);
}

type CriticalActionFailedEvent = Extract<
  ProductEvent,
  { name: "critical_action_failed" }
>;
type CriticalActionFailedProps = CriticalActionFailedEvent["properties"];

/**
 * Capture a non-throwing critical action failure. Triggers session replay
 * via the `critical_action_failed` event configured in the PostHog project.
 * Use only when the failure path does not throw — throwing failures should
 * propagate to `captureClientException` instead. See
 * docs/observability/POSTHOG_SESSION_REPLAY_DESIGN.md.
 */
export function captureCriticalActionFailed(
  reason: string,
  properties: Omit<CriticalActionFailedProps, "reason">
): void {
  captureClient({
    name: "critical_action_failed",
    properties: { reason, ...properties },
  });
}

/**
 * Returns PostHog session/distinct-id headers for outbound API requests, or
 * `null` if the SDK has not loaded yet. Lets `src/services/api.ts` propagate
 * the browser session id to the server without importing `posthog-js`
 * directly (preserves the `no-restricted-imports` boundary from SKR-600).
 *
 * On the server side, `instrumentation.ts#onRequestError` and `middleware.ts`
 * read these headers to attach `$session_id` to server-emitted `$exception`
 * events, so server and client errors share a single replay timeline.
 */
export function getClientPostHogHeaders(): Record<string, string> | null {
  if (!posthog.__loaded) return null;
  const sessionId = posthog.get_session_id?.();
  const distinctId = posthog.get_distinct_id?.();
  const headers: Record<string, string> = {};
  if (sessionId) headers["X-POSTHOG-SESSION-ID"] = sessionId;
  if (distinctId) headers["X-POSTHOG-DISTINCT-ID"] = distinctId;
  return Object.keys(headers).length > 0 ? headers : null;
}
